'use client'

import { useState } from 'react'
import type { DragEvent } from 'react'
import { Plus } from 'lucide-react'
import type { Task, Sprint } from '@/lib/types'
import {
  sprintWeeks, weekOfSprint, weekStartDate, taskInSprint, riskFlags, isoDay, type RiskCtx,
} from '@/lib/dev-sprint'
import { useToast } from '@/context/ToastContext'
import type { BoardData, Filters, DevDerived } from './types'
import { taskMatchesFilters, inAnySprint } from './filter'
import { TaskCard } from './TaskCard'
import { Avatar } from './ui'

const UNASSIGNED = '__unassigned__'
const norm = (x?: string | null) => (x ?? '').trim().toLowerCase()
const fmtShort = (iso: string) => new Date(iso + 'T00:00:00').toLocaleDateString('id-ID', { day: 'numeric', month: 'short' })

interface Props {
  board: BoardData
  sprint: Sprint
  derived: DevDerived
  riskCtx: RiskCtx
  filters: Filters
  isLead: boolean
  readOnly: boolean
  meUsername: string | null
  canEditTaskFn: (assignee: string | null) => boolean
  onOpenTask: (id: string) => void
  onAddTask: (devUsername: string | null, week: number) => void
  onChanged: () => void
}

export function TimelineView({
  board, sprint, derived, riskCtx, filters, isLead, readOnly, meUsername, canEditTaskFn, onOpenTask, onAddTask, onChanged,
}: Props) {
  const { toast } = useToast()
  const [dragId, setDragId] = useState<string | null>(null)
  const [overKey, setOverKey] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const weeks = sprintWeeks(sprint)
  const weekCols = Array.from({ length: weeks }, (_, i) => i + 1)

  const rows: { key: string; email: string; name: string; username: string | null }[] = board.team.map((m) => ({
    key: norm(m.username), email: m.email, name: m.name, username: m.username,
  }))

  const cells = new Map<string, Task[]>()
  const push = (k: string, t: Task) => { const a = cells.get(k) ?? []; a.push(t); cells.set(k, a) }
  let hasUnassigned = false
  for (const t of board.tasks) {
    if (!taskMatchesFilters(t, filters, derived, riskCtx)) continue
    const devKey = norm(t.assignee) || UNASSIGNED
    if (devKey === UNASSIGNED) hasUnassigned = true
    if (taskInSprint(t, sprint)) {
      const w = weekOfSprint(t.dueDate, sprint)
      // Anggota sprint tapi belum punya minggu (deadline kosong/di luar window) → Backlog lane.
      push(w ? `${devKey}::${w}` : `${devKey}::b`, t)
    } else if (!inAnySprint(t, board.sprints)) {
      push(`${devKey}::b`, t)
    }
  }
  const allRows = hasUnassigned ? [...rows, { key: UNASSIGNED, email: UNASSIGNED, name: 'Belum di-assign', username: null }] : rows

  // Drop: pindah minggu (dueDate) + (lead) reassign kalau beda baris dev.
  const handleDrop = async (targetUsername: string | null, colKey: string) => {
    const id = dragId
    setDragId(null); setOverKey(null)
    if (!id || busy || readOnly) return
    if (!targetUsername) return // baris "belum di-assign" bukan target
    const t = board.tasks.find((x) => x.id === id)
    if (!t) return
    if (!canEditTaskFn(t.assignee)) { toast('Tidak berwenang menggeser task ini', 'error'); return }
    if (!isLead && norm(targetUsername) !== norm(meUsername)) { toast('Hanya bisa menggeser task di barismu sendiri', 'info'); return }
    // Pindah ke Backlog menghapus deadline → konfirmasi dulu kalau ada deadline.
    if (colKey === 'b' && isoDay(t.dueDate) && !window.confirm('Pindah ke Backlog akan menghapus deadline task ini. Lanjut?')) return

    const body: Record<string, unknown> = {}
    body.dueDate = colKey === 'b' ? null : weekStartDate(sprint, Number(colKey))
    if (isLead && norm(targetUsername) !== norm(t.assignee)) body.assignee = targetUsername
    if (colKey === 'b' && String(t.dueDate ?? '').slice(0, 10) === '' && !body.assignee) return

    setBusy(true)
    try {
      const res = await fetch(`/api/dev/tasks/${id}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      })
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Gagal')
      onChanged()
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Gagal', 'error')
    } finally { setBusy(false) }
  }

  const onDragStart = (t: Task) => (e: DragEvent) => {
    if (readOnly || !canEditTaskFn(t.assignee)) { e.preventDefault(); return }
    setDragId(t.id)
    e.dataTransfer.effectAllowed = 'move'
  }

  const cardFor = (t: Task) => {
    const p = derived.projById.get(t.projectId)
    const draggable = !readOnly && canEditTaskFn(t.assignee)
    return (
      <TaskCard
        key={t.id}
        task={t}
        projectName={p?.name ?? '—'}
        projectColor={p?.color ?? '#D4A843'}
        status={derived.statusOf(t)}
        risks={riskFlags(t, riskCtx)}
        onClick={() => onOpenTask(t.id)}
        draggable={draggable}
        onDragStart={onDragStart(t)}
        onDragEnd={() => { setDragId(null); setOverKey(null) }}
      />
    )
  }

  const gridCols = `160px repeat(${weeks}, minmax(190px, 1fr)) minmax(190px, 1fr)`

  return (
    <div className="rounded-2xl border border-[#E7E5DF] bg-white overflow-x-auto">
      <div style={{ display: 'grid', gridTemplateColumns: gridCols }} className="min-w-max">
        <div className="sticky left-0 z-10 bg-[#FAF9F6] border-b border-r border-[#E7E5DF] px-3 py-2.5 text-[11px] font-bold uppercase tracking-wide text-[#8B8A85]">Dev</div>
        {weekCols.map((w) => (
          <div key={w} className="border-b border-[#E7E5DF] px-3 py-2.5">
            <div className="text-[12px] font-bold text-[#1A1916]">Week {w}</div>
            <div className="text-[10px] text-[#AEADA8] tabular-nums">{fmtShort(weekStartDate(sprint, w))}</div>
          </div>
        ))}
        <div className="border-b border-l border-[#E7E5DF] px-3 py-2.5 text-[12px] font-bold text-[#8B8A85] bg-[#FAF9F6]" title="Task tanpa deadline / belum terjadwal di minggu mana pun">Backlog · tanpa tgl</div>

        {allRows.map((row) => {
          const dropCell = (colKey: string, week: number | null) => {
            const key = `${row.key}::${colKey}`
            const list = cells.get(key) ?? []
            const isTarget = !!row.username && !readOnly
            return (
              <div
                onDragOver={(e) => { if (dragId && isTarget) { e.preventDefault(); setOverKey(key) } }}
                onDragLeave={() => setOverKey((k) => (k === key ? null : k))}
                onDrop={(e) => { e.preventDefault(); handleDrop(row.username, colKey) }}
                className={`border-b border-[#EDEBE5] p-2 space-y-2 min-h-[92px] transition-colors ${overKey === key ? 'bg-[#FBF6E9]' : ''}`}
              >
                {list.map((t) => cardFor(t))}
                {isLead && !readOnly && week !== null && row.key !== UNASSIGNED && (
                  <button
                    onClick={() => onAddTask(row.username, week)}
                    className="w-full flex items-center justify-center gap-1 py-1.5 rounded-lg border border-dashed border-[#DBD9D2] text-[11px] text-[#AEADA8] hover:text-[#1A1916] hover:border-[#C9C7C0] transition-colors"
                  >
                    <Plus className="w-3 h-3" /> Task
                  </button>
                )}
              </div>
            )
          }
          return (
            <div key={row.email} className="contents">
              <div className="sticky left-0 z-10 bg-white border-b border-r border-[#E7E5DF] px-3 py-3 flex items-center gap-2">
                {row.username ? <Avatar name={row.name} size={26} /> : <span className="w-[26px] h-[26px] rounded-full bg-[#F0EEE7] flex-shrink-0" />}
                <span className="text-[13px] font-semibold text-[#1A1916] truncate">{row.name}</span>
              </div>
              {weekCols.map((w) => <div key={w}>{dropCell(String(w), w)}</div>)}
              <div className="border-l border-[#EDEBE5]">{dropCell('b', null)}</div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
